<template>
    <Dropdown
        class="RPDropdownButton"
        :dropdown-position="dropdownPosition"
        :disabled="disabled"
        :modal-header="modalHeader"
    >
        <template v-slot="{open, toggleDropdown}">
            <ToggleButton
                :open="open"
                :option="selectedOption"
                :disabled="disabled"
                :type="type"
                :icon-color="defaultIconColor"
                :hide-arrow="hideArrow"
                :status="status"
                @click="toggleDropdown"
            >
                <template v-slot:default>
                    <!-- @slot Replace the text of the dropdown toggle button-->
                    <slot
                        :open="open"
                        :toggleDropdown="toggleDropdown"
                    ></slot>
                </template>
            </ToggleButton>
        </template>
        <template v-slot:dropdown="{open, close, isModal}">
            <!-- @slot Replace the contents of the dropdown -->
            <slot
                name="dropdown"
                :isModal="isModal"
                :close="close"
                :open="open"
            >
                <div class="RPDropdownButton__dropdown">
                    <template v-for="opt in optionsFormatted">
                        <!--
                            @slot Replace the individual dropdown items
                            @binding {object} option An object containing properties `label` and `id`
                        -->
                        <slot
                            name="option"
                            :option="opt"
                        >
                            <Option
                                :key="opt.label"
                                :option="opt"
                                :is-active="opt.value === selectedOption.value"
                                :show-selection="showSelection"
                                :required="required"
                                @click="val=>optionClick(val,close)"
                            />
                        </slot>
                    </template>
                </div>
            </slot>
        </template>
    </Dropdown>
</template>
<script>
import Option from './_option.vue';
import ToggleButton from './_toggle-button.vue';
import colors from 'style/_colors.scss';
import isEqual from 'lodash/isEqual';
import Dropdown from '@/components/Basic/Elements/Dropdown.vue';
import stat from 'status';

export default {
    name: 'RPDropdownButton',
    components: {
        Option,
        ToggleButton,
        Dropdown
    },
    props: {
        /**
         * @values primary, secondary, tertiary
         */
        type: {
            type: String,
            default: 'primary',
            validator: val => ['primary', 'secondary', 'tertiary'].indexOf(val) !== -1,
        },
        /**
         * @model
        */
        value: {
            type: [String, Boolean, Number, Object],
            default: -9999
        },
        /**
         * An array of numbers, strings, or objects with `value` and `label` properties:
         * {value: opt, label: opt}
         */
        options: {
            type: Array,
            default: () => ([]),
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * When true, a value must be selected. When false, the selected
         * option can be clicked to unselect it.
         */
        required: {
            type: Boolean,
            default: false,
        },
        /**
         * A label to show when no option is selected.
         */
        defaultLabel: {
            type: String,
            default: 'Select',
        },
        /**
         * An icon to show when no option is selected.
         * Instructions for importing individual Streamline components can be found in the Streamline app.
         */
        defaultIcon: {
            type: [Object, Array],
            default: () => ({}),
        },
        /**
         * TODO: implement
         */
        defaultIconSize: {
            type: Number,
            default: 16,
        },
        /**
         * Any CSS color
         */
        defaultIconColor: {
            type: String,
            default: colors.grey600,
        },
        dropdownPosition: {
            type: String,
            default: 'right',
            validator: val => ['left', 'right', 'center'].indexOf(val) !== -1
        },
        /**
         * Show the currently selected option instead of the `defaultLabel`.
         */
        showSelection: {
            type: Boolean,
            default: true,
        },
        showDropdown: {
            type: Boolean,
            default: true,
        },
        hideArrow: {
            type: Boolean,
            default: false,
        },
        modalHeader: {
            type: String,
            default: 'Select an option...'
        },
        status: {
            type: String,
            default: stat.STANDBY
        },
    },
    computed: {
        optionsFormatted(){
            return this.options.map(opt => {
                return (typeof opt === 'string'
                        || typeof opt === 'number')
                    ? {
                        value: opt,
                        label: opt,
                    }
                    : opt;
            });
        },
        selectedOption(){
            const temp = this.optionsFormatted.find(opt => isEqual(opt.value, this.value));
            return (this.showSelection && !!temp)
                ? temp
                : {
                    label: this.defaultLabel,
                    icon: this.defaultIcon,
                    iconSize: this.defaultIconSize,
                };
        },
    },
    methods: {
        optionClick(val, close){
            this.$emit('input', val);
            close();

        }
    }
};
</script>
<style lang="scss">
    .RPDropdownButton {
        position: relative;
        &__dropdown {
            border: 1px solid $grey-100;
            box-sizing: border-box;
            box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
            border-radius: 4px;
            margin-top: 0.25rem;
            display: flex;
            flex-direction: column;
            background: $white;
        }
    }
</style>

<docs>
Basic example:

```vue
<RPDropdownButton
    :options="['Apple', 'Banana', 'Orange']"
    v-model="myVal"
></RPDropdownButton>
```


```vue
import { House1 } from "@reviewpush/streamline-regular/lib/interface-essential/Home";
<RPDropdownButton
    :options="['Apple', 'Banana', 'Orange']"
    :defaultIcon="House1"
    v-model="myVal"
></RPDropdownButton>
```

</docs>
